import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import palette from '@ui/palette';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';

interface FoldersBreadcrumbProps {
  drive?: string;
  path: Array<string>;
}

const useTrailItemStyles = makeStyles()(theme => ({
  container: {
    minWidth: 0,
    '&::before': {
      content: "'/'",
      position: 'absolute',
      fontSize: theme.spacing(1.5),
      lineHeight: theme.spacing(2),
    },
  },
  item: {
    display: 'flex',
    marginLeft: theme.spacing(1.5),
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  name: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fullWidth: {
    width: '100%',
  },
}));

const FoldersBreadcrumbTrailItem = ({
  name,
  showIcon = true,
  fullWidth = true,
}: {
  name: string;
  showIcon?: boolean;
  fullWidth?: boolean;
}) => {
  const { classes, cx } = useTrailItemStyles();

  return (
    <div className={cx(classes.container, { [classes.fullWidth]: fullWidth })}>
      <div className={classes.item}>
        {showIcon && <Icon type="folder" />}
        <Typography variant="pMd" className={classes.name}>
          {name}
        </Typography>
      </div>
    </div>
  );
};

const useStyles = makeStyles()(theme => ({
  container: {
    color: palette.gray_75,
    display: 'grid',
    gap: theme.spacing(0.5),
    gridAutoFlow: 'column',
    justifyContent: 'start',
  },
}));

/**
 * Presentational component similar to a breadcrumb
 * Required - Pass in list of folder names - first path will be root and all following will be treated as sub paths
 * Optional - will append which drive is at the root
 *
 *
 * As of now - the last item in list will have priority to be displayed
 * and all other paths will be collapsed into ellipsis
 */
const FoldersBreadcrumb = (props: FoldersBreadcrumbProps) => {
  const { drive, path } = props;
  const { classes } = useStyles();

  const destinationParentFolder = path.at(-1);
  const hasMultipleFolders = path.length > 1;

  return (
    <Box className={classes.container}>
      {drive && <Typography variant="pMd">{`${drive} workspace`}</Typography>}
      {hasMultipleFolders && (
        <FoldersBreadcrumbTrailItem
          name="..."
          showIcon={false}
          fullWidth={false}
        />
      )}
      {destinationParentFolder && (
        <FoldersBreadcrumbTrailItem name={destinationParentFolder} />
      )}
    </Box>
  );
};

export default FoldersBreadcrumb;
